<script setup lang="ts">
import { computed, onMounted, ref, onBeforeUnmount, nextTick, watch } from 'vue'
import { useWindowSize, useDebounceFn } from '@vueuse/core'
import AutoHeight from 'embla-carousel-auto-height'
import Autoplay from 'embla-carousel-autoplay'
import { useSiteStore } from '~/stores/site'
import { usePromotionalStore } from '~/stores/promotional'
import { useImage } from '#imports'

const siteStore = useSiteStore()
const promotionalStore = usePromotionalStore()
const img = useImage()
const { width } = useWindowSize()

// Get slides from store - prioritize realtime data
const slidesFromStore = computed(() => {
  try {
    // Only use promotional store (realtime) - no fallback to site store
    // Use activePromotionalSlides to filter out expired slides based on start/end dates
    if (promotionalStore.activePromotionalSlides && promotionalStore.activePromotionalSlides.length > 0) {
      return promotionalStore.activePromotionalSlides
    }
    
    console.warn('[HeroSlider] No slides found in realtime store')
    return []
  } catch (error) {
    console.warn('[HeroSlider] Error accessing stores:', error)
    return []
  }
})

// Adapt slides to the format expected by the template
const adaptedSlides = computed(() => {
  return slidesFromStore.value.map((slide: any, index: number) => {
    const desktopSrc = slide.src || slide.image || slide.desktop;
    const mobileSrc = slide.mobile || slide.mobile_img || slide.image || slide.src;
    const hasVideo = !!slide.video;
    const videoPoster = slide.desktop_poster || desktopSrc || mobileSrc;

    return {
      id: slide.id || slide.title || `slide-${index}`, // Fallback for key
      src: desktopSrc, 
      mobile: mobileSrc, 
      alt: slide.alt || slide.title || slide.heading_content || 'Hero slide image',
      title: slide.title || slide.heading_content || ' ',
      subTitle: slide.subTitle || slide.sub_heading || ' ',
      hasVideo: hasVideo,
      video: slide.video || '',
      videoPoster: videoPoster,
      ...slide 
    }
  })
})

const api = ref<null | any>(null)
const current = ref(1)

// Initialize AutoHeight plugin
const autoHeightPlugin = AutoHeight()

// Initialize Autoplay plugin
const autoplayPlugin = Autoplay({
  delay: 5000,
  stopOnInteraction: false,
})

const progressBarInner = ref<HTMLElement | null>(null)

// Refs for animated elements
const titleRefs = ref<(HTMLElement | null)[]>([])
const subtitleRefs = ref<(HTMLElement | null)[]>([])
const button1Refs = ref<(HTMLElement | null)[]>([])
const button2Refs = ref<(HTMLElement | null)[]>([])

// Debounced function to reinitialize Embla and its plugins
const debouncedReInit = useDebounceFn(() => {
  if (api.value) {
    api.value.reInit();
    // updateProgressBar() and triggerAnimations() are usually called by Embla's own 'reInit' or 'select' events, 
    // but explicitly calling them after a manual reInit might be needed if AutoHeight doesn't trigger 'select'.
    // Let's rely on Embla's internal events first.
  }
}, 200); // Debounce for 200ms

// Watch for width changes to re-initialize the carousel
watch(width, () => {
  if (process.client) {
    debouncedReInit();
  }
});

// Watch for slide data changes to re-initialize the carousel
watch(adaptedSlides, (newSlides, oldSlides) => {
  if (process.client && newSlides.length !== oldSlides?.length) {
    debouncedReInit();
  }
}, { deep: true });

// Watch for promotional store updates to trigger re-initialization
watch(() => promotionalStore.lastUpdate, (newUpdate, oldUpdate) => {
  if (process.client && newUpdate !== oldUpdate) {
    // Small delay to ensure the computed properties have updated
    nextTick(() => {
      debouncedReInit();
    });
  }
});

// Set the carousel API from the Carousel component
function setApi(val: any) {
  api.value = val
}

// Compute total slides based on the API with fallback to adapted slides
const totalCount = computed(() => {
  const apiCount = api.value ? api.value.scrollSnapList().length : 0
  const slidesCount = adaptedSlides.value.length
  
  // Use the most accurate count - if API count differs from slides count, prefer slides count
  // as it's the most up-to-date after real-time updates
  if (apiCount > 0 && apiCount === slidesCount) {
    return apiCount
  }
  
  return slidesCount
})

// Compute dots for navigation
const dots = computed(() => {
  return Array.from({ length: totalCount.value }, (_, i) => i + 1)
})

// Navigate to a specific slide
function goToSlide(index: number) {
  if (api.value) {
    api.value.scrollTo(index)
  }
}

// Use requestAnimationFrame for optimized progress bar updates
let animationFrame: number | null = null

function updateProgressBar() {
  if (!api.value || !progressBarInner.value) return

  const progress = Math.max(0, Math.min(1, api.value.scrollProgress()))
  if (animationFrame !== null) {
    cancelAnimationFrame(animationFrame)
  }
  animationFrame = requestAnimationFrame(() => {
    progressBarInner.value!.style.transform = `translateX(${progress * 100}%)`
  })
}

// Update the current slide index
function updateCurrent() {
  if (api.value) {
    current.value = api.value.selectedScrollSnap() + 1
  }
}

// Handle individual image/video load events
function onImageLoad() {
  if (api.value) {
    api.value.reInit() // Reinitialize to allow AutoHeight to adjust based on the newly loaded image
    updateProgressBar()
    triggerAnimations() // Trigger animations after re-initialization
  }
}

// Function to trigger animations on the active slide
function triggerAnimations() {
  nextTick(() => {
    const index = current.value - 1

    // Titles
    const title = titleRefs.value[index]
    if (title) {
      title.classList.remove('animate-fadeInUp')
      void title.offsetWidth // Trigger reflow to reset the animation
      title.classList.add('animate-fadeInUp')
    }

    // Subtitles
    const subtitle = subtitleRefs.value[index]
    if (subtitle) {
      subtitle.classList.remove('animate-fadeInUp', 'delay-200')
      void subtitle.offsetWidth
      subtitle.classList.add('animate-fadeInUp', 'delay-200')
    }

    // First Button
    const button1 = button1Refs.value[index]
    if (button1) {
      button1.classList.remove('animate-fadeInUp', 'delay-400')
      void button1.offsetWidth
      button1.classList.add('animate-fadeInUp', 'delay-400')
    }

    // Second Button
    const button2 = button2Refs.value[index]
    if (button2) {
      button2.classList.remove('animate-fadeInUp', 'delay-600')
      void button2.offsetWidth
      button2.classList.add('animate-fadeInUp', 'delay-600')
    }
  })
}

onMounted(() => {
  if (api.value) {
    api.value.on('select', () => {
      updateCurrent()
      updateProgressBar()
      triggerAnimations()
    })
    api.value.on('scroll', updateProgressBar)
    api.value.on('reInit', () => {
      updateCurrent()
      updateProgressBar()
      triggerAnimations()
    })
  }

  // Initialize progress bar if needed
  if (api.value) {
    updateProgressBar()
  }

  // Trigger animations for the initial slide
  triggerAnimations()

  // Initial call to re-calculate height if needed, though AutoHeight should handle initial setup
  // debouncedReInit(); // Might not be needed here if AutoHeight works on init

  if (api.value) {
    // Ensure reInit also updates progress and animations if needed
    api.value.on('reInit', () => {
      updateCurrent()      // Added to ensure current slide indicator is correct
      updateProgressBar()  // Ensure progress bar updates
      triggerAnimations()  // Ensure animations re-trigger for the current slide
    });
  }
})

onBeforeUnmount(() => {
  // Clean up event listeners and cancel any pending animation frames
  if (api.value) {
    api.value.off('select', updateCurrent)
    api.value.off('scroll', updateProgressBar)
    api.value.off('reInit', updateProgressBar)
  }
  if (animationFrame !== null) {
    cancelAnimationFrame(animationFrame)
  }
})
</script>


<template>
  <div>
    <div class="w-full bg-black overflow-hidden">
      <!-- Enhanced Realtime Status Indicator (Client Only to prevent hydration mismatch) -->
      <ClientOnly>
        <div class="absolute top-4 right-4 z-10">
          <div class="flex items-center gap-2 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1">
            <div 
              :class="[
                'w-2 h-2 rounded-full transition-colors duration-300',
                {
                  'bg-green-400': promotionalStore.connectionStatus === 'connected',
                  'bg-yellow-400': promotionalStore.connectionStatus === 'reconnecting',
                  'bg-orange-400': promotionalStore.connectionStatus === 'fallback',
                  'bg-red-400': promotionalStore.connectionStatus === 'error',
                  'bg-gray-400': promotionalStore.connectionStatus === 'disconnected'
                }
              ]"
            ></div>
            <span class="text-xs text-white font-medium">
              <template v-if="promotionalStore.connectionStatus === 'connected'">
                🟢 Live
              </template>
              <template v-else-if="promotionalStore.connectionStatus === 'reconnecting'">
                🟡 Reconnecting...
              </template>
              <template v-else-if="promotionalStore.connectionStatus === 'fallback'">
                🟠 Fallback
              </template>
              <template v-else-if="promotionalStore.connectionStatus === 'error'">
                🔴 Error
              </template>
              <template v-else>
                ⚪ Offline
              </template>
            </span>
            
            <!-- Connection health indicator for detailed status -->
            <div 
              v-if="promotionalStore.connectionStatus !== 'connected'"
              :class="[
                'w-1 h-1 rounded-full ml-1',
                {
                  'bg-green-300': promotionalStore.connectionHealth === 'excellent',
                  'bg-yellow-300': promotionalStore.connectionHealth === 'good',
                  'bg-orange-300': promotionalStore.connectionHealth === 'fair',
                  'bg-red-300': promotionalStore.connectionHealth === 'poor',
                  'bg-gray-300': promotionalStore.connectionHealth === 'critical'
                }
              ]"
              :title="`Connection health: ${promotionalStore.connectionHealth} (${promotionalStore.connectionHealthScore}%)`"
            ></div>
          </div>
          
          <!-- Extended status tooltip on hover -->
          <div 
            v-if="promotionalStore.connectionStatus !== 'connected'"
            class="absolute top-full right-0 mt-1 bg-black/80 backdrop-blur-sm rounded px-2 py-1 text-xs text-white whitespace-nowrap opacity-0 hover:opacity-100 transition-opacity duration-200"
          >
            {{ promotionalStore.statusMessage }}
            <template v-if="promotionalStore.isReconnecting">
              <br>{{ promotionalStore.reconnectAttempts }}/{{ promotionalStore.maxReconnectAttempts }} attempts
            </template>
          </div>
        </div>
      </ClientOnly>
      
      <div class="relative">
        <Carousel
          class="w-full"
          :opts="{
            loop: true,
          }"
          :plugins="[autoHeightPlugin, autoplayPlugin]"
          @init-api="setApi"
          @mouseenter="autoplayPlugin.stop"
          @mouseleave="autoplayPlugin.play"
        >
          <CarouselContent>
            <CarouselItem v-for="(slide, index) in adaptedSlides" :key="slide.id">
              <div class="flex flex-col md:block">
                <section class="relative w-full overflow-hidden min-h-[200px] max-h-[600px] md:min-h-[500px] md:max-h-[800px]">
                  <div class="absolute inset-0">
                    <!-- Video Content -->
                    <div v-if="slide.hasVideo" class="relative w-full h-full min-h-[200px] md:min-h-[500px]">
                      <video
                        class="absolute top-0 left-0 w-full h-full object-cover"
                        :poster="slide.videoPoster ? img(slide.videoPoster) : undefined" 
                        autoplay
                        muted
                        loop
                        playsinline
                        @loadeddata="onImageLoad"
                      >
                        <source :src="slide.video" type="video/mp4">
                        Your browser does not support the video tag.
                      </video>
                    </div>
                    <!-- Image Content -->
                    <picture v-else>
                      <!-- Mobile Source -->
                      <source
                        media="(max-width: 767px)"
                        :srcset="slide.mobile ? img(slide.mobile, { format: 'webp' }) : undefined" 
                        sizes="100vw" 
                      />
                      <!-- Desktop Source -->
                      <source
                        media="(min-width: 768px)"
                        :srcset="slide.src ? img(slide.src, { format: 'webp' }) : undefined"
                        sizes="100vw" 
                      />
                      <!-- Fallback <img> tag -->
                      <img
                        :src="slide.src ? img(slide.src) : (slide.mobile ? img(slide.mobile) : '')" 
                        :alt="slide.alt"
                        class="w-full h-auto transition-transform duration-200 ease-out"
                        loading="lazy"
                        @load="onImageLoad"
                        fetchpriority="auto" 
                        :style="{ objectFit: 'cover', width: '100%', height: '100%' }" 
                      />
                    </picture>
                  </div>
                  <!-- Caption for desktop -->
                  <div class="caption hidden md:flex absolute inset-0 flex-col justify-center px-4">
                    <div class="container mx-auto">
                      <div class="text-left max-w-screen-sm">
                        <h2
                          ref="el => titleRefs.value[index] = el"
                          class="mt-1 text-6xl text-white font-bold font-heading animate-fadeInUp"
                          v-html="slide.title"
                        >
                        </h2>
                        <div
                          ref="el => subtitleRefs.value[index] = el"
                          class="text-xl font-bold text-white mb-4 animate-fadeInUp delay-200"
                          v-html="slide.subTitle"
                        >
                        </div>
                        <div>
                          <a
                            ref="el => button1Refs.value[index] = el"
                            class="inline-block w-auto px-12 py-2 text-lg text-black uppercase font-bold bg-yellow-400 hover:opacity-75 transition duration-200 animate-fadeInUp delay-400"
                            href="#"
                          >
                            Find out more
                          </a>
                        </div>
                        <div class="mt-3">
                          <a
                            ref="el => button2Refs.value[index] = el"
                            class="inline-block w-auto px-12 py-2 text-lg text-black uppercase font-bold bg-yellow-400 hover:backdrop-blur-sm transition duration-200 animate-fadeInUp delay-600"
                            href="#"
                          >
                            View Offers
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
                <!-- Caption for mobile -->
                <div class="caption md:hidden bg-black px-4 py-8">
                  <div class="container mx-auto">
                    <div class="text-center">
                      <h2
                        ref="el => titleRefs.value[index] = el"
                        class="mt-1 text-3xl text-white font-bold font-heading animate-fadeInUp"
                        v-html="slide.title"
                      >
                      </h2>
                      <div
                        ref="el => subtitleRefs.value[index] = el"
                        class="text-base font-bold text-white mb-4 animate-fadeInUp delay-200"
                        v-html="slide.subTitle"
                      >
                      </div>
                      <div class="mt-4">
                        <a
                          ref="el => button1Refs.value[index] = el"
                          class="inline-block w-full px-8 py-3 text-base text-black uppercase font-bold bg-yellow-400 hover:opacity-75 transition duration-200 animate-fadeInUp delay-400"
                          href="#"
                        >
                          Find out more
                        </a>
                      </div>
                      <div class="mt-3">
                        <a
                          ref="el => button2Refs.value[index] = el"
                          class="inline-block w-full px-8 py-3 text-base text-black uppercase font-bold bg-yellow-400 hover:backdrop-blur-sm transition duration-200 animate-fadeInUp delay-600"
                          href="#"
                        >
                          View Offers
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>
          </CarouselContent>
          <CarouselPrevious
            class="absolute w-10 h-10 md:w-14 md:h-14 left-4 border-none top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white text-white hover:text-black"
            @mouseenter="autoplayPlugin.stop"
            @mouseleave="autoplayPlugin.play"
          />
          <CarouselNext
            class="absolute w-10 h-10 md:w-14 md:h-14 right-4 border-none top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white text-white hover:text-black"
            @mouseenter="autoplayPlugin.stop"
            @mouseleave="autoplayPlugin.play"
          />

          <!-- Progress Bar -->
          <div class="sm:absolute bottom-0 left-0 right-0 h-1 bg-red-500">
            <div
              ref="progressBarInner"
              class="h-full bg-white dark:bg-black transition-transform duration-200 ease-out"
              style="transform: translateX(-100%)"
            />
          </div>
        </Carousel>

        <!-- Slide Counter -->
        <div class="absolute bottom-4 left-1/2 -translate-x-1/2 py-2 px-4 bg-white/70 rounded-full">
          <span class="text-sm text-black">
            Slide {{ current }} of {{ totalCount }}
          </span>
        </div>
      </div>
    </div>

    <!-- Navigation Dots -->
    <div class="flex justify-center mt-4 pb-4 md:pb-0">
      <button
        v-for="dot in dots"
        :key="dot"
        class="w-3 h-3 mx-1 rounded-full transition-colors duration-200 ease-out"
        :class="current === dot ? 'bg-white' : 'bg-gray-500 hover:bg-gray-300'"
        @click="goToSlide(dot - 1)"
        aria-label="Go to slide {{ dot }}"
      />
    </div>
  </div>
</template>


<style scoped>
.carousel-item {
  transition: transform 0.5s ease;
  height: 100vh;
}
.carousel-previous svg,
.carousel-next svg {
  color: white; /* Default color */
}

.carousel-previous:hover svg,
.carousel-next:hover svg {
  color: black; /* Hover color */
}
.bg-red-500 {
  background-color: #ffba00; /* Custom red class */
}

/* Prevent carousel from collapsing by setting a minimum height */
section {
  transition: min-height 0.3s ease;
  height: 100vh;
}

@media (max-width: 767px) {
  section {
    min-height: 200px;
    height: 100vh;
  }
}

@media (min-width: 768px) {
  section {
    min-height: 500px;
    height: 100vh;
  }
}

video {
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style> 